<template>
    <Modal 
        v-model="showModal" 
        :mask-closable="false" 
        :closable="true" 
        :transfer="false"
        append-to-body
        width="700" 
        class-name="receipt-modal" 
        @on-cancel="handleClose"
    >
        
        <div id="receipt-content" class="receipt-content tw-bg-white" style="font-family: 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif; width: 148mm; height: 210mm; margin: 0 auto; background-color: white; padding-left: 4mm; padding-right: 4mm; box-sizing: border-box; overflow: hidden;">
            <!-- Header -->
            <div class="tw-text-center tw-mb-1 tw-border-b-2 tw-border-gray-800 tw-pb-1">
                <h2 class="tw-text-xl tw-font-bold tw-mb-1">DAILY COLLECTION CLOSEOUT RECEIPT</h2>
                <p class="tw-text-xs tw-text-gray-600">Receipt #{{ receipt.id }} | {{ receipt.cleared_at }}</p>
            </div>

            <!-- Branch and CO - Compact -->
            <div class="tw-mb-2 tw-text-sm tw-bg-gray-100 tw-p-2 tw-rounded" style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
                <div><span class="tw-font-semibold">Branch:</span> <span class="tw-font-bold">{{ receipt.branch.code }}</span></div>
                <div><span class="tw-font-semibold">Date:</span> <span class="tw-font-bold">{{ receipt.report_date }}</span></div>
                <div><span class="tw-font-semibold">CO Name:</span> <span class="tw-font-bold">{{ receipt.co.name }}</span></div>
                <div><span class="tw-font-semibold">CO ID:</span> <span class="tw-font-bold">{{ receipt.co.code }}</span></div>
            </div>

            <!-- Disbursement Section - Compact -->
            <div class="tw-mb-2">
                <p class="tw-text-sm tw-font-bold tw-mb-1 tw-uppercase tw-bg-blue-100 tw-p-1 tw-text-center">DISBURSEMENT TODAY</p>
                <div class="tw-text-xs" style="display: grid; grid-template-columns: 1fr 1fr; gap: 4px;">
                    <div class="tw-flex tw-justify-between tw-px-1">
                        <span>Requested:</span>
                        <span class="tw-font-semibold">{{ formatNumber(receipt.amounts.loan_requested) }}</span>
                    </div>
                    <div class="tw-flex tw-justify-between tw-px-1">
                        <span>Approved:</span>
                        <span class="tw-font-semibold">{{ formatNumber(receipt.amounts.disbursement) }}</span>
                    </div>
                    <div class="tw-flex tw-justify-between tw-px-1 tw-bg-orange-100 tw-border tw-border-orange-300">
                        <span class="tw-font-semibold tw-text-orange-700">Returned:</span>
                        <span class="tw-font-bold tw-text-orange-700">{{ formatNumber(receipt.amounts.returned) }}</span>
                    </div>
                    <div class="tw-flex tw-justify-between tw-px-1 tw-bg-gray-100">
                        <span class="tw-font-semibold">Given:</span>
                        <span class="tw-font-bold">{{ formatNumber(receipt.amounts.actual_disbursed) }}</span>
                    </div>
                </div>
                <div class="tw-flex tw-justify-between tw-bg-yellow-50 tw-p-1 tw-mt-1 tw-text-xs">
                    <span class="tw-font-semibold">Fees Collected:</span>
                    <span class="tw-font-bold">{{ formatNumber(receipt.amounts.disburse_collected) }}</span>
                </div>
            </div>

            <!-- Collection Section - Compact -->
            <div class="tw-mb-2">
                <p class="tw-text-sm tw-font-bold tw-mb-1 tw-uppercase tw-bg-green-100 tw-p-1 tw-text-center">REPAYMENT COLLECTED</p>
                <div class="tw-flex tw-justify-between tw-font-semibold tw-bg-green-50 tw-p-1 tw-text-sm">
                    <span>Total Repayment:</span>
                    <span class="tw-text-green-700 tw-font-bold">{{ formatNumber(receipt.amounts.actual_collected, '៛') }}</span>
                </div>
            </div>

            <div class="tw-border-t tw-border-gray-400 tw-my-2"></div>

            <!-- What's Being Cleared - Compact -->
            <div class="tw-bg-green-50 tw-border-2 tw-border-green-600 tw-p-1 tw-rounded">
                <p class="tw-text-sm tw-text-center tw-mb-1 tw-font-bold tw-text-green-700 tw-uppercase">✓ CLEARED & RECEIVED</p>
                
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
                    <!-- Cash -->
                    <div class="tw-bg-white tw-p-2 tw-rounded tw-border tw-border-green-300">
                        <p class="tw-text-xs tw-font-semibold tw-mb-1 tw-text-gray-700">💵 CASH:</p>
                        <div class="tw-text-xs">
                            <div class="tw-flex tw-justify-between tw-mb-1">
                                <span>KHR:</span>
                                <span class="tw-font-bold tw-text-green-700">{{ formatNumber(receipt.amounts.cash_khr, '៛') }}</span>
                            </div>
                            <div class="tw-flex tw-justify-between">
                                <span>USD:</span>
                                <span class="tw-font-bold tw-text-green-700">{{ formatNumber(receipt.amounts.cash_usd, '$') }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Bank Transfers -->
                    <div class="tw-bg-white tw-p-2 tw-rounded tw-border tw-border-green-300">
                        <p class="tw-text-xs tw-font-semibold tw-mb-1 tw-text-gray-700">🏦 BANK:</p>
                        <div class="tw-text-xs">
                            <div class="tw-mb-1">
                                <p class="tw-font-semibold" style="font-size: 10px;">ABA:</p>
                                <div class="tw-flex tw-justify-between">
                                    <span>KHR:</span>
                                    <span class="tw-font-bold tw-text-green-700">{{ formatNumber(receipt.payment_methods.bank_aba.khr, '៛') }}</span>
                                </div>
                                <div class="tw-flex tw-justify-between">
                                    <span>USD:</span>
                                    <span class="tw-font-bold tw-text-green-700">{{ formatNumber(receipt.payment_methods.bank_aba.usd, '$') }}</span>
                                </div>
                            </div>
                            <div style="font-size: 10px;">
                                <p class="tw-font-semibold">ACLEDA:</p>
                                <div class="tw-flex tw-justify-between">
                                    <span>KHR:</span>
                                    <span class="tw-font-bold tw-text-green-700">{{ formatNumber(receipt.payment_methods.bank_acleda.khr, '៛') }}</span>
                                </div>
                                <div class="tw-flex tw-justify-between">
                                    <span>USD:</span>
                                    <span class="tw-font-bold tw-text-green-700">{{ formatNumber(receipt.payment_methods.bank_acleda.usd, '$') }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="tw-mt-2 tw-pt-1 tw-border-t tw-border-green-300 tw-text-center">
                    <small class="tw-text-xs tw-text-center tw-text-gray-700">
                        <span class="tw-font-semibold">Note:</span> Cash cleared. Bank transfers documented.
                    </small>
                </div>
            </div>

            <!-- Footer -->
            <div class="tw-mt-3">
                <!-- Signatures -->
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 12px;">
                    <!-- CO Signature -->
                    <div class="tw-text-center">
                        <div class="tw-border-b tw-border-gray-400 tw-mb-1" style="height: 40px;"></div>
                        <p class="tw-text-xs tw-font-semibold">CO Signature</p>
                        <p class="tw-text-xs tw-text-gray-600">{{ receipt.co.name }}</p>
                    </div>
                    
                    <!-- Receiver Signature -->
                    <div class="tw-text-center">
                        <div class="tw-border-b tw-border-gray-400 tw-mb-1" style="height: 40px;"></div>
                        <p class="tw-text-xs tw-font-semibold">Received By</p>
                        <p class="tw-text-xs tw-text-gray-600">{{ receipt.received_by.name }}</p>
                    </div>
                </div>
                
                <div class="tw-text-center tw-text-xs tw-text-gray-500 tw-border-t tw-border-dashed tw-border-gray-400">
                    <p class="tw-font-semibold">Keep this receipt for your records</p>
                </div>
            </div>
        </div>

        <div slot="footer" class="tw-flex tw-justify-end tw-gap-2">
            <Button type="success" icon="ios-download" @click="downloadAsImage" size="small">Download Image</Button>
            <Button type="info" icon="ios-copy" @click="copyImageToClipboard" size="small">Copy Image</Button>
            <Button type="primary" icon="ios-print" @click="printReceipt" size="small">Print</Button>
            <Button type="default" @click="handleClose" size="small">Close</Button>
        </div>
    </Modal>
</template>

<script>
export default {
    name: "ReceiptModal",
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        receipt: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            showModal: this.visible
        };
    },
    watch: {
        visible: {
            immediate: true,
            handler(val) {
                this.showModal = val;
            }
        },
        showModal(val) {
            this.$emit('update:visible', val);
            if (!val) {
                this.$emit('close');
            }
        }
    },
    methods: {
        formatNumber(num, currency = "") {
            if ((!num && num !== 0) || num === 0) return currency ? `${currency} 0` : "0";
            let val = new Intl.NumberFormat("en-US", { 
                minimumFractionDigits: currency === "$" ? 2 : 0, 
                maximumFractionDigits: 2 
            }).format(num);
            return currency ? `${currency} ${val}` : val;
        },
        
        handleClose() {
            this.showModal = false;
        },
        
        closeModal() {
            this.showModal = false;
        },
        
        async downloadAsImage() {
            try {
                const loading = this.$Message.loading({
                    content: 'Generating image...',
                    duration: 0
                });
                
                const element = document.getElementById('receipt-content');
                const html2canvas = (await import('html2canvas')).default;
                const canvas = await html2canvas(element, {
                    backgroundColor: '#ffffff',
                    scale: 2,
                    logging: false,
                    useCORS: true,
                    width: 559, // 148mm at 96dpi
                    height: 794, // 210mm at 96dpi
                    imageTimeout: 0,
                    removeContainer: true
                });
                
                loading();
                
                canvas.toBlob((blob) => {
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    const filename = `receipt_A5_${this.receipt.id}_${new Date().getTime()}.png`;
                    link.download = filename;
                    link.href = url;
                    link.click();
                    URL.revokeObjectURL(url);
                    this.$Message.success('Receipt downloaded successfully');
                }, 'image/png', 1.0);
            } catch (error) {
                console.error('Error downloading image:', error);
                this.$Message.error('Failed to download receipt image');
            }
        },
        
        async copyImageToClipboard() {
            try {
                const loading = this.$Message.loading({
                    content: 'Copying image...',
                    duration: 0
                });
                
                const element = document.getElementById('receipt-content');
                const html2canvas = (await import('html2canvas')).default;
                const canvas = await html2canvas(element, {
                    backgroundColor: '#ffffff',
                    scale: 2,
                    logging: false,
                    useCORS: true,
                    width: 559,
                    height: 794,
                    imageTimeout: 0,
                    removeContainer: true
                });
                
                canvas.toBlob(async (blob) => {
                    try {
                        if (navigator.clipboard && navigator.clipboard.write) {
                            // eslint-disable-next-line no-undef
                            const item = new ClipboardItem({ 'image/png': blob });
                            await navigator.clipboard.write([item]);
                            loading();
                            this.$Message.success('Receipt image copied to clipboard!');
                        } else {
                            loading();
                            this.$Message.warning('Clipboard not supported. Use Download instead.');
                        }
                    } catch (clipboardError) {
                        loading();
                        console.error('Error copying to clipboard:', clipboardError);
                        this.$Message.error('Failed to copy image. Try Download instead.');
                    }
                }, 'image/png', 1.0);
            } catch (error) {
                console.error('Error generating image:', error);
                this.$Message.error('Failed to generate receipt image');
            }
        },
        
        printReceipt() {
            try {
                // Get the receipt content HTML
                const receiptContent = document.getElementById('receipt-content');
                const receiptHTML = receiptContent.outerHTML;
                
                // Create a new window for printing
                const printWindow = window.open('', '', 'width=800,height=1000');
                
                // Write the HTML with styles
                printWindow.document.write(`
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <title>Receipt #${this.receipt.id}</title>
                        <style>
                            @page {
                                size: A5;
                                margin: 0;
                            }
                            body {
                                margin: 0;
                                padding: 0;
                                font-family: 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
                            }
                            .receipt-content {
                                width: 148mm;
                                height: 210mm;
                                margin: 0 auto;
                                padding: 8mm;
                                box-sizing: border-box;
                                background-color: white;
                            }
                            /* Force print background colors */
                            * {
                                -webkit-print-color-adjust: exact !important;
                                print-color-adjust: exact !important;
                                color-adjust: exact !important;
                            }
                            /* Tailwind-like utilities */
                            .tw-text-center { text-align: center; }
                            .tw-text-left { text-align: left; }
                            .tw-text-xl { font-size: 1.25rem; }
                            .tw-text-sm { font-size: 0.875rem; }
                            .tw-text-xs { font-size: 0.75rem; }
                            .tw-font-bold { font-weight: 700; }
                            .tw-font-semibold { font-weight: 600; }
                            .tw-mb-1 { margin-bottom: 0.25rem; }
                            .tw-mb-2 { margin-bottom: 0.5rem; }
                            .tw-mb-3 { margin-bottom: 0.75rem; }
                            .tw-mb-4 { margin-bottom: 1rem; }
                            .tw-mt-2 { margin-top: 0.5rem; }
                            .tw-mt-3 { margin-top: 0.75rem; }
                            .tw-p-1 { padding: 0.25rem; }
                            .tw-p-2 { padding: 0.5rem; }
                            .tw-p-3 { padding: 0.75rem; }
                            .tw-px-1 { padding-left: 0.25rem; padding-right: 0.25rem; }
                            .tw-pt-1 { padding-top: 0.25rem; }
                            .tw-pb-2 { padding-bottom: 0.5rem; }
                            .tw-border { border-width: 1px; border-style: solid; }
                            .tw-border-2 { border-width: 2px; border-style: solid; }
                            .tw-border-t { border-top-width: 1px; border-top-style: solid; }
                            .tw-border-b { border-bottom-width: 1px; border-bottom-style: solid; }
                            .tw-border-b-2 { border-bottom-width: 2px; border-bottom-style: solid; }
                            .tw-border-gray-800 { border-color: #1f2937 !important; }
                            .tw-border-gray-400 { border-color: #9ca3af !important; }
                            .tw-border-gray-300 { border-color: #d1d5db !important; }
                            .tw-border-orange-300 { border-color: #fdba74 !important; }
                            .tw-border-green-600 { border-color: #16a34a !important; }
                            .tw-border-green-300 { border-color: #86efac !important; }
                            .tw-border-dashed { border-style: dashed !important; }
                            .tw-border-solid { border-style: solid !important; }
                            .tw-rounded { border-radius: 0.25rem; }
                            .tw-bg-white { background-color: white !important; }
                            .tw-bg-gray-100 { background-color: #f3f4f6 !important; }
                            .tw-bg-blue-100 { background-color: #dbeafe !important; }
                            .tw-bg-orange-100 { background-color: #ffedd5 !important; }
                            .tw-bg-yellow-50 { background-color: #fefce8 !important; }
                            .tw-bg-green-50 { background-color: #f0fdf4 !important; }
                            .tw-bg-green-100 { background-color: #dcfce7 !important; }
                            .tw-text-gray-600 { color: #4b5563 !important; }
                            .tw-text-gray-700 { color: #374151 !important; }
                            .tw-text-gray-500 { color: #6b7280 !important; }
                            .tw-text-orange-700 { color: #c2410c !important; }
                            .tw-text-green-700 { color: #15803d !important; }
                            .tw-uppercase { text-transform: uppercase; }
                            .tw-flex { display: flex; }
                            .tw-justify-between { justify-content: space-between; }
                            @media print {
                                body { margin: 0; padding: 0; }
                                .receipt-content { margin: 0; }
                                * {
                                    -webkit-print-color-adjust: exact !important;
                                    print-color-adjust: exact !important;
                                    color-adjust: exact !important;
                                }
                            }
                        </style>
                    </head>
                    <body>
                        ${receiptHTML}
                    </body>
                    </html>
                `);
                
                printWindow.document.close();
                
                // Wait for content to load then print
                setTimeout(() => {
                    printWindow.focus();
                    printWindow.print();
                    printWindow.close();
                }, 500);
                
            } catch (error) {
                console.error('Error printing receipt:', error);
                this.$Message.error('Failed to print receipt');
            }
        }
    }
};
</script>

<style scoped>
.receipt-modal ::v-deep .ivu-modal-body {
    max-height: 80vh;
    overflow-y: auto;
}
</style>
